import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NotAvailableState } from "@/components/ui/system-state";

/**
 * Reconciliation Runs tile — design port (ALL-5825, dashboard lane task 4).
 *
 * The prototype tile renders a `HighlightBarChart` of matched records per
 * completed run, an in-progress run with a `Progress` bar, and a
 * Runs / Unmatched / Breaks summary. Every one of those numbers comes from
 * `getReconRunHistory()` and `RECON_CURRENT_RUN`, which are fixtures in
 * `features/oversight/oversight-data`.
 *
 * There is no reconciliation endpoint behind them in this app: `src/lib/api.ts`
 * serves only the unrelated provider-migration routes under
 * `/v1/reconciliation/*`, and the designed target `/reconciliation/run-history`
 * is a `DeferredPage` for the same reason (`monitoring-chain.tsx` records the
 * same decision for chain monitoring). Rendering the fixture chart here would
 * put fabricated matched-record counts and break totals on the home dashboard —
 * exactly the fabricated-UI case ALL-4747 G6 forbids — so the tile keeps the
 * designed header and renders the designed not-available state instead.
 * Nothing on this tile is estimated or inferred.
 *
 * The header link is kept as designed: it points at the deferred
 * `/reconciliation/run-history` page, which states the same absence, and the
 * not-available body is the guard that stops the tile reading as a live chart.
 */
export function ReconRuns() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Reconciliation Runs</h2>
          <div className="mt-0.5 text-xs text-muted">Matched records per completed run</div>
        </div>
        <Link
          to="/reconciliation/run-history"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          History <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      <NotAvailableState
        className="flex-1 justify-center"
        title="Reconciliation runs are not available yet"
        description="Matched records, unmatched records and breaks per run are not collected in this environment, so no run chart or run totals are shown here."
        reason="No gateway route serves reconciliation run history — the /v1/reconciliation/* routes cover provider migration only."
      />
    </div>
  );
}
